import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { isAfter, isFuture, isPast, isToday } from 'date-fns';

import { Calendar } from '../ui';

import { DatePickerMode, DatePickerValue } from './DatePicker.types';
import DatePickerCalendar from './DatePickerCalendar';
import DatePickerContent from './DatePickerContent';
import { DatePickerLabel, DatePickerLabelProps } from './DatePickerLabel';
import { DatePickerPopper } from './DatePickerPopper';
import { useDatePickerStrategy } from './useDatePickerStrategy.hook';

export type DatePickerProps = Omit<
  React.ComponentProps<typeof Calendar>,
  'mode'
> &
  DatePickerLabelProps & {
    className?: string;
    value?: DatePickerValue;
    disabled?: boolean;
    mode?: DatePickerMode;
    numberOfMonths?: number;
    fixedRangeDays?: number;
    shouldExcludeFuture?: boolean;
    shouldExcludePast?: boolean;
    disableDatesAfter?: Date;
    shouldDisplayTime?: boolean;
    placeholder?: string;
    onChange?: (value?: DatePickerValue) => void;
    onApply?: (value: DatePickerValue) => void;
    onClear?: () => void;
    displayInUTC?: boolean;
  };

const DatePicker: FC<DatePickerProps> = ({
  label,
  infoTooltipContent,
  isRequired,
  className,
  disabled = false,
  value,
  onChange,
  placeholder = 'Pick a date',
  mode = DatePickerMode.SINGLE,
  numberOfMonths = 1,
  shouldExcludeFuture = false,
  shouldExcludePast = false,
  disableDatesAfter,
  fixedRangeDays,
  shouldDisplayTime = true,
  onApply,
  onClear,
  displayInUTC = false,
  ...rest
}) => {
  const strategy = useDatePickerStrategy({
    value,
    onApply,
    onChange,
    onClear,
    fixedRangeDays,
    shouldExcludeFuture,
    shouldExcludePast,
    disableDatesAfter,
    mode,
    displayInUTC
  });
  const [isOpen, setIsOpen] = useState(false);

  const isDatePickerDisabled = useCallback(
    (date: Date) => {
      if (disableDatesAfter && isAfter(date, disableDatesAfter)) {
        return true;
      }

      const shouldDisableFutureDates = shouldExcludeFuture && isFuture(date);
      const shouldDisablePastDates = shouldExcludePast && isPast(date);

      if (
        (shouldDisableFutureDates || shouldDisablePastDates) &&
        !isToday(date)
      ) {
        return true;
      }

      return false;
    },
    [shouldExcludeFuture, shouldExcludePast, disableDatesAfter]
  );

  const hasActions = useMemo(() => {
    return onApply !== undefined || onClear !== undefined || shouldDisplayTime;
  }, [onApply, onClear, shouldDisplayTime]);

  const handleOpenChange = (open: boolean) => {
    if (!open && onApply) {
      strategy.onValueChange();
    }
    setIsOpen(open);
  };

  const handleApply = () => {
    strategy.apply();
    setIsOpen(false);
  };

  return (
    <div className="w-full">
      <DatePickerLabel
        label={label}
        infoTooltipContent={infoTooltipContent}
        isRequired={isRequired}
        disabled={disabled}
      />
      <DatePickerPopper
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        disabled={disabled}
        className={className}
        hasActions={hasActions}
        placeholder={placeholder}
        anchorText={strategy.getDisplayDate()}
      >
        <DatePickerContent
          displayInUTC={displayInUTC}
          fromValue={strategy.fromValue}
          toValue={strategy.toValue}
          onClear={onClear ? strategy.clear : undefined}
          onApply={onApply ? handleApply : undefined}
          isApplyDisabled={strategy.isApplyDisabled}
          onOptionChange={(time) => strategy.changeTime?.({ time })}
          onFromTimeChange={strategy.onFromTimeChange}
          onToTimeChange={strategy.onToTimeChange}
          shouldDisplayTime={shouldDisplayTime}
          mode={mode}
        >
          <DatePickerCalendar
            mode={mode}
            value={strategy.value}
            numberOfMonths={numberOfMonths}
            disabled={isDatePickerDisabled}
            onChange={strategy.select}
            {...rest}
          />
        </DatePickerContent>
      </DatePickerPopper>
    </div>
  );
};

export default DatePicker;
